import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  Modal,
  Select,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import React, { useState, useEffect } from 'react';
import { CheckOutlined, WarningOutlined } from '@ant-design/icons';
import { useTheme } from '@mui/material/styles';
import { ReactComponent as CalculatorIcon } from '../../assets/images/icons/calculator.svg';
import { ReactComponent as ChevronIcon } from '../../assets/images/icons/chevron.svg';

import GlobalDialog from '../../components/@extended/GlobalDialog';
import LoadingButton from '../../components/@extended/LoadingButton';
import { CopierEditDialogProps } from '../../types/copier';
import { sizeByBreakpoint, textSizeByBreakpoint } from '../../utils/helper';
import { RISK_TYPE, UpdateSlaveReq } from '../../proto/trade-copier_pb';
import useAuth from '../../hooks/useAuth';
import { useTradeClient } from '../../client/client-hook';
import { useDispatch } from '../../store';
import { DispatchSnackbarService } from '../../services';
import RiskCalculatorModal from './components/RiskCalculatorModal';

const riskTypeMapFromDisplayText: any = {
  'Fixed Lot': 1,
  'Lot Multiplier': 2,
  'Balance Multiplier': 3,
  'Fixed Balance Multiplier': 4,
  'Fixed % risk': 5
};

const filter_type_map: any = {
  COPY: 1,
  IGNORE: 2,
  '': 0
};

const CopierEditDialog = ({ isOpen, onClose, selectedSlave, selectedMaster, onSuccess }: CopierEditDialogProps) => {
  const theme = useTheme();
  const { user } = useAuth();
  const tradeClient = useTradeClient();
  const dispatch = useDispatch();
  const service = new DispatchSnackbarService();
  const [loading, setLoading] = useState(false);
  const [openRiskCalculator, setOpenRiskCalculator] = useState(false);
  const [expandedPanel, setExpandedPanel] = useState<string>('panel1');
  const [riskType, setRiskType] = useState<number | ''>('');
  const [riskSettings, setRiskSettings] = useState('');
  const [riskSettingsFixedLot, setRiskSettingsFixedLot] = useState('');
  const [riskSettingsBalancePercentage, setRiskSettingsBalancePercentage] = useState('');
  const [fixedMasterBalance, setFixedMasterBalance] = useState('');
  const [copyStopLoss, setCopyStopLoss] = useState(false);
  const [copyTakeProfit, setCopyTakeProfit] = useState(false);
  const [copyPendingOrders, setCopyPendingOrders] = useState(false);
  const [fixedStopLoss, setFixedStopLoss] = useState('No');
  const [fixedStopLossValue, setFixedStopLossValue] = useState('');
  const [fixedStopLossError, setFixedStopLossError] = useState('');
  const [fixedTakeProfit, setFixedTakeProfit] = useState('No');
  const [fixedTakeProfitValue, setFixedTakeProfitValue] = useState('');
  const [fixedTakeProfitError, setFixedTakeProfitError] = useState('');
  const [stopLossRefinement, setStopLossRefinement] = useState('No');
  const [stopLossRefinementValue, setStopLossRefinementValue] = useState('');
  const [stopLossRefinementError, setStopLossRefinementError] = useState('');
  const [takeProfitRefinement, setTakeProfitRefinement] = useState('No');
  const [takeProfitRefinementValue, setTakeProfitRefinementValue] = useState('');
  const [takeProfitRefinementError, setTakeProfitRefinementError] = useState('');
  const [forceMinimumLotSize, setForceMinimumLotSize] = useState(false);
  const [forceMaximumLotSize, setForceMaximumLotSize] = useState(false);
  const [reverseTrade, setReverseTrade] = useState(false);
  const [contractAlignment, setContractAlignment] = useState(false);
  const [copyExpiryTime, setCopyExpiryTime] = useState(false);
  const [strictClose, setStrictClose] = useState(false);
  const [copyTradeComment, setCopyTradeComment] = useState('No');
  const [customComment, setCustomComment] = useState('');
  const [copyMagicNumber, setCopyMagicNumber] = useState('No');
  const [magicNumber, setMagicNumber] = useState('');
  const [delayType, setDelayType] = useState('no');
  const [tradeDelayMin, setTradeDelayMin] = useState('');
  const [tradeDelayMax, setTradeDelayMax] = useState('');
  const [lotRefiner, setLotRefiner] = useState('0');
  const [filterByMagicNumber, setFilterByMagicNumber] = useState('');
  const [filterByComment, setFilterByComment] = useState('');
  const [tradeSide, setTradeSide] = useState('ALL');
  const [filterByLotSize, setFilterByLotSize] = useState('No');
  const [minLotFilter, setMinLotFilter] = useState('');
  const [maxLotFilter, setMaxLotFilter] = useState('');
  const [selectedMagicNumbers, setSelectedMagicNumbers] = useState<string[]>([]);
  const [currentMagicNumber, setCurrentMagicNumber] = useState('');
  const [selectedComments, setSelectedComments] = useState<string[]>([]);
  const [currentComment, setCurrentComment] = useState('');

  const handleAccordionChange = (panel: string) => {
    setExpandedPanel(expandedPanel === panel ? '' : panel);
  };

  const handleSave = async () => {
    if (!user?.tcId || !selectedSlave?.id) {
      service.dispatchSnackbar(dispatch, 'Missing required data', 'alert', 'error', 3000);
      return;
    }

    // Validate Fixed Stop Loss, Fixed Take Profit, and Refinements
    let hasErrors = false;
    const slError = fixedStopLoss === 'Yes' && (!fixedStopLossValue || Number(fixedStopLossValue) <= 0);
    const tpError = fixedTakeProfit === 'Yes' && (!fixedTakeProfitValue || Number(fixedTakeProfitValue) <= 0);
    const slRefError = stopLossRefinement === 'Yes' && (!stopLossRefinementValue || Number(stopLossRefinementValue) === 0);
    const tpRefError = takeProfitRefinement === 'Yes' && (!takeProfitRefinementValue || Number(takeProfitRefinementValue) === 0);

    if (slError) {
      setFixedStopLossError('Fixed stop loss is required');
      hasErrors = true;
    } else {
      setFixedStopLossError('');
    }

    if (tpError) {
      setFixedTakeProfitError('Fixed take profit is required');
      hasErrors = true;
    } else {
      setFixedTakeProfitError('');
    }

    if (slRefError) {
      setStopLossRefinementError('Stop loss refinement is required');
      hasErrors = true;
    } else {
      setStopLossRefinementError('');
    }

    if (tpRefError) {
      setTakeProfitRefinementError('Take profit refinement is required');
      hasErrors = true;
    } else {
      setTakeProfitRefinementError('');
    }

    if (hasErrors) {
      const errorParts: string[] = [];
      if (slError) errorParts.push('Fixed SL');
      if (tpError) errorParts.push('Fixed TP');
      if (slRefError) errorParts.push('SL Refinement');
      if (tpRefError) errorParts.push('TP Refinement');
      
      const errorMessage = errorParts.length === 1 
        ? `${errorParts[0]} value required`
        : `${errorParts.join(' and ')} values required`;
      service.dispatchSnackbar(dispatch, errorMessage, 'alert', 'error', 3000);
      return;
    }

    setLoading(true);
    try {
      const client = await tradeClient();

      const updateSlaveReq: UpdateSlaveReq.AsObject = {
        userId: user.tcId,
        configId: selectedSlave.configId || '',
        slaveId: selectedSlave.id,
        enable: { value: selectedSlave?.status === 'right' },
        riskType: riskType as any,
        lotMultiplierValue: {
          value: riskType === RISK_TYPE.LOT_MULTIPLIER ? Number(riskSettings) : 0
        },
        fixedLotValue: {
          value: riskType === RISK_TYPE.FIXED_LOT ? Number(riskSettingsFixedLot) : 0
        },
        balanceMultiplierValue: {
          value: riskType === RISK_TYPE.BALANCE_MULTIPLIER ? Number(riskSettings) : 0
        },
        balancePercentageValue: {
          value: riskType === RISK_TYPE.BALANCE_PERCENTAGE ? Number(riskSettingsBalancePercentage) : 0
        },
        copyTakeProfit: { value: copyTakeProfit },
        copyStopLoss: { value: copyStopLoss },
        copyPendingOrders: { value: copyPendingOrders },
        forceMinimumLotSize: { value: forceMinimumLotSize },
        forceMaximumLotSize: { value: forceMaximumLotSize },
        copyComment: { value: copyTradeComment !== 'No' },
        customComment: { value: customComment || '' },
        reverseTrade: { value: reverseTrade },
        strictClose: { value: strictClose },
        considerContractSize: { value: contractAlignment },
        copyMagicNumber: { value: copyMagicNumber !== 'No' },
        magicNumber: { value: copyMagicNumber === 'Custom' ? Number(magicNumber) : 0 },
        lotRefinerValue: { value: Number(lotRefiner) || 0 },
        magicFilterType: filter_type_map[filterByMagicNumber] || filter_type_map[''],
        magicFiltersList: selectedMagicNumbers.map((num) => ({ value: Number(num) })),
        copyExpiryTime: { value: copyExpiryTime },
        commentFilterType: filter_type_map[filterByComment] || filter_type_map[''],
        commentFiltersList: selectedComments.map((comment) => ({ value: comment })),
        minLotFilter: {
          value: filterByLotSize === 'Yes' && minLotFilter ? Number(minLotFilter) : null
        } as any,
        maxLotFilter: {
          value: filterByLotSize === 'Yes' && maxLotFilter ? Number(maxLotFilter) : null
        } as any,
        stopLossPip: {
          value: fixedStopLoss === 'Yes' && fixedStopLossValue ? Number(fixedStopLossValue) : 0
        },
        takeProfitPip: {
          value: fixedTakeProfit === 'Yes' && fixedTakeProfitValue ? Number(fixedTakeProfitValue) : 0
        },
        slRefiner: {
          value: stopLossRefinement === 'Yes' && stopLossRefinementValue ? Number(stopLossRefinementValue) : 0
        },
        tpRefiner: {
          value: takeProfitRefinement === 'Yes' && takeProfitRefinementValue ? Number(takeProfitRefinementValue) : 0
        },
        manageExisting: selectedSlave?.slaveData?.manageExisting,
        allowedTradeSide: { value: tradeSide === 'ALL' ? null : tradeSide } as any,
        copyExistingTrades: {
          value: selectedSlave?.slaveData?.copyExistingTrades?.value || false
        },
        tradingHoursList: selectedSlave?.slaveData?.tradingHoursList || []
      };

      // Handle delay times
      if (delayType === 'random') {
        if (tradeDelayMin !== '' && tradeDelayMin !== undefined) {
          updateSlaveReq.minDelayTime = { value: Number(tradeDelayMin) };
        }
        if (tradeDelayMax !== '' && tradeDelayMax !== undefined) {
          updateSlaveReq.maxDelayTime = { value: Number(tradeDelayMax) };
        }
      } else if (delayType === 'fixed') {
        if (tradeDelayMin !== '' && tradeDelayMin !== undefined) {
          updateSlaveReq.minDelayTime = { value: Number(tradeDelayMin) };
          updateSlaveReq.maxDelayTime = { value: Number(tradeDelayMin) };
        }
      } else if (delayType === 'no') {
        updateSlaveReq.minDelayTime = { value: 0 };
        updateSlaveReq.maxDelayTime = { value: 0 };
      }

      // Handle Fixed Balance Multiplier
      if (riskType === RISK_TYPE.FIXED_BALANCE_MULTIPLIER) {
        updateSlaveReq.fixedBalanceMultiplierValue = {
          value: Number(riskSettings)
        };
        updateSlaveReq.fixedMasterBalance = {
          value: Number(fixedMasterBalance)
        };
      }

      await client.updateSlave(updateSlaveReq as any);

      service.dispatchSnackbar(dispatch, 'Slave configuration updated successfully', 'alert', 'success', 3000);
      setLoading(false);
      if (onClose) {
        onClose();
      }
      if (onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      console.error('Error updating slave:', err);
      setLoading(false);
      const message = err.message === 'no permission to access this RPC' ? 'Permission denied' : err.message;
      service.dispatchSnackbar(dispatch, message, 'alert', 'error', 5000);
    }
  };

  // Populate form with selectedSlave data when dialog opens
  useEffect(() => {
    if (isOpen && selectedSlave) {
      // Risk Settings
      const mappedRiskType = selectedSlave?.riskType ? riskTypeMapFromDisplayText[selectedSlave?.riskType] : '';
      setRiskType(mappedRiskType);

      // Set risk setting values based on risk type
      const riskSettingValue =
        selectedSlave?.riskType === 'Fixed Balance Multiplier'
          ? (selectedSlave.slaveData?.fixedBalanceMultiplierValue?.value ?? selectedSlave.slaveData?.fixedBalanceMultiplierValue)
          : Number(selectedSlave?.riskSetting?.slice(0, -1));

      setRiskSettings(riskSettingValue?.toString() || '');
      setRiskSettingsFixedLot(selectedSlave?.riskSetting?.slice(0, -4) || '');
      setRiskSettingsBalancePercentage(selectedSlave?.slaveData?.balancePercentageValue?.value?.toString() || '');
      setFixedMasterBalance(
        selectedSlave?.riskType === 'Fixed Balance Multiplier' 
          ? (selectedSlave.slaveData?.fixedMasterBalance?.value ?? selectedSlave.slaveData?.fixedMasterBalance)?.toString() 
          : ''
      );

      // SL and TP Settings
      setCopyStopLoss(selectedSlave?.additionalProps?.copyStopLoss || false);
      setCopyTakeProfit(selectedSlave?.additionalProps?.copyTakeProfit || false);
      setCopyPendingOrders(selectedSlave?.additionalProps?.copyPendingOrders || false);

      const fixedSLValue = selectedSlave?.additionalProps?.fixedStopLossValue || 0;
      setFixedStopLoss(fixedSLValue > 0 ? 'Yes' : 'No');
      setFixedStopLossValue(fixedSLValue || '');

      const fixedTPValue = selectedSlave?.additionalProps?.fixedTakeProfitValue || 0;
      setFixedTakeProfit(fixedTPValue > 0 ? 'Yes' : 'No');
      setFixedTakeProfitValue(fixedTPValue || '');

      const slRefinementValue = selectedSlave?.additionalProps?.stopLossRefinementValue || 0;
      setStopLossRefinement(slRefinementValue != 0 ? 'Yes' : 'No');
      setStopLossRefinementValue(slRefinementValue || '');

      const tpRefinementValue = selectedSlave?.additionalProps?.takeProfitRefinementValue || 0;
      setTakeProfitRefinement(tpRefinementValue != 0 ? 'Yes' : 'No');
      setTakeProfitRefinementValue(tpRefinementValue || '');

      // Advanced Settings
      setForceMinimumLotSize(selectedSlave?.additionalProps?.forceMinimumLotSize || false);
      setForceMaximumLotSize(selectedSlave?.additionalProps?.forceMaximumLotSize || false);
      setReverseTrade(selectedSlave?.additionalProps?.reverseTrade || false);
      setContractAlignment(selectedSlave?.additionalProps?.considerContractSize || false);
      setCopyExpiryTime(selectedSlave?.additionalProps?.copyExpiryTime || false);
      setStrictClose(selectedSlave?.additionalProps?.strictClose || false);

      // Copy Trade Comment
      const customCommentValue = selectedSlave?.additionalProps?.customComment || '';
      setCustomComment(customCommentValue);
      setCopyTradeComment(
        selectedSlave?.additionalProps?.copyComment && customCommentValue
          ? 'Custom'
          : selectedSlave?.additionalProps?.copyComment
          ? 'Yes'
          : 'No'
      );

      const magicNum = selectedSlave?.additionalProps?.magicNumber || '';
      setMagicNumber(magicNum);
      setCopyMagicNumber(
        selectedSlave?.additionalProps?.copyMagicNumber && magicNum && magicNum !== '0'
          ? 'Custom'
          : selectedSlave?.additionalProps?.copyMagicNumber
          ? 'Yes'
          : 'No'
      );

      // Delay type
      const minDelay = selectedSlave?.additionalProps?.tradeDelayMin || 0;
      const maxDelay = selectedSlave?.additionalProps?.tradeDelayMax || 0;
      setTradeDelayMin(minDelay || '');
      setTradeDelayMax(maxDelay || '');

      if (minDelay === 0 && maxDelay === 0) {
        setDelayType('no');
      } else if (minDelay === maxDelay) {
        setDelayType('fixed');
      } else {
        setDelayType('random');
      }

      setLotRefiner(selectedSlave?.additionalProps?.lotRefiner || '0');

      // Trade Filters
      const magicFilterType = selectedSlave?.slaveData?.magicFilterType?.value;
      setFilterByMagicNumber(magicFilterType === 'UNKNOWN' ? '' : magicFilterType || '');

      const commentFilterType = selectedSlave?.slaveData?.commentFilterType?.value;
      setFilterByComment(commentFilterType === 'UNKNOWN' ? '' : commentFilterType || '');

      setTradeSide(selectedSlave?.slaveData?.allowedTradeSide?.value || 'ALL');

      const minLot = selectedSlave?.additionalProps?.minLotFilter;
      const maxLot = selectedSlave?.additionalProps?.maxLotFilter;
      setMinLotFilter(minLot || '');
      setMaxLotFilter(maxLot || '');
      setFilterByLotSize(minLot && maxLot ? 'Yes' : 'No');

      // Magic numbers and comments
      const magicFilters = selectedSlave?.slaveData?.magicFiltersList || selectedSlave?.magicFilterList || [];
      if (magicFilters?.length > 0) {
        setSelectedMagicNumbers(magicFilters.map((e: any) => e.value?.toString()));
      } else {
        setSelectedMagicNumbers([]);
      }

      const commentFilters = selectedSlave?.slaveData?.commentFiltersList || selectedSlave?.commentFilterList || [];
      if (commentFilters?.length > 0) {
        setSelectedComments(commentFilters.map((e: any) => e.value));
      } else {
        setSelectedComments([]);
      }
    }
  }, [isOpen, selectedSlave]);

  const accordionStyle = {
    border: '1px solid',
    borderColor: theme.palette.border.main,
    backgroundColor: theme.palette.background.default,
    borderRadius: '5px',
    overflow: 'hidden'
  };

  const accordionSummaryStyle = {
    backgroundColor: 'transparent',
    '& .MuiAccordionSummary-expandIconWrapper': {
      order: 1,
      transition: 'transform 0.3s ease'
    },
    '& .MuiAccordionSummary-content': {
      order: 2,
      marginLeft: 0
    },
    '&.Mui-expanded .MuiAccordionSummary-expandIconWrapper': {
      transform: 'rotate(-180deg)'
    }
  };

  const accordionDetailsStyle = { border: 'none', paddingTop: 1 };

  const inputStyle = {
    backgroundColor: theme.palette.background.paper,
    borderColor: theme.palette.divider,
    fontSize: textSizeByBreakpoint(16),
    borderRadius: '6px',
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: theme.palette.divider
      },
      '&:hover fieldset': {
        borderColor: theme.palette.primary.main
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.main
      }
    }
  };

  return (
    <GlobalDialog
      open={isOpen}
      onClose={onClose}
      title="Edit Slave"
      subtitle="Showing the list of all the accounts."
      footer={
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} flexWrap="wrap">
          <Box display="flex" alignItems="center" gap={1}>
            <Box
              component={CalculatorIcon}
              onClick={() => setOpenRiskCalculator(true)}
              sx={{
                width: { xs: 26, sm: 28, md: 30, lg: 32 },
                height: { xs: 26, sm: 28, md: 30, lg: 32 },
                cursor: 'pointer'
              }}
            />
            <Typography variant="body2" color="text.secondary" fontSize={{ md: 14, sm: 13, xs: 12 }} lineHeight="100%">
              Calculate your risk setting,{' '}
              <Typography
                variant="body2"
                component="span"
                color="primary"
                sx={{ cursor: 'pointer' }}
                fontSize={{ md: 14, sm: 13, xs: 12 }}
                lineHeight="100%"
                onClick={() => setOpenRiskCalculator(true)}
              >
                click here
              </Typography>
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={2}>
            <Button
              sx={{
                // padding: sizeByBreakpoint(0.5, 10),
                borderRadius: '6px',
                fontWeight: 600,
                fontSize: sizeByBreakpoint(1, 16),
                height: sizeByBreakpoint(1, 40),
                color: (theme) => (theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.text.secondary),
                borderColor: (theme) => (theme.palette.mode === 'dark' ? theme.palette.grey[600] : theme.palette.grey[300]),
                '&:hover': {
                  borderColor: (theme) => (theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.grey[500]),
                  backgroundColor: (theme) => (theme.palette.mode === 'dark' ? theme.palette.action.hover : theme.palette.grey[50])
                }
              }}
              disableElevation
              fullWidth
              variant="outlined"
              color="secondary"
              onClick={onClose}
            >
              Cancel
            </Button>

            <LoadingButton
              sx={{
                borderRadius: '6px',
                fontWeight: 600,
                fontSize: sizeByBreakpoint(1, 16),
                height: sizeByBreakpoint(1, 40)
              }}
              loadingPosition="center"
              loading={loading}
              disableElevation
              disabled={loading || !riskType}
              fullWidth
              type="submit"
              variant="contained"
              color="primary"
              onClick={handleSave}
            >
              <CheckOutlined />
              Confirm
            </LoadingButton>
          </Box>
        </Box>
      }
      maxWidth="sm"
    >
      <Box display="flex" flexDirection="column" gap={2}>
        <Accordion expanded={expandedPanel === 'panel1'} onChange={() => handleAccordionChange('panel1')} sx={accordionStyle}>
          <AccordionSummary sx={accordionSummaryStyle} expandIcon={<ChevronIcon />} aria-controls="panel1-content" id="panel1-header">
            <Typography component="span" color="textPrimary" fontSize={{ xs: 13, sm: 14, md: 15, lg: 16 }} fontWeight="600">
              Risk Settings
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={accordionDetailsStyle}>
            <Box width="100%" display="flex" flexDirection={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'start', sm: 'start' }} gap={2}>
              <Box flex={1} width="100%">
                <Typography
                  variant="subtitle2"
                  fontWeight={500}
                  mb={1}
                  color="textSecondary"
                  fontSize={{ xs: 13, sm: 14, md: 15, lg: 16 }}
                  lineHeight="100%"
                >
                  Risk Type
                </Typography>
                <FormControl fullWidth>
                  <Select
                    variant="outlined"
                    fullWidth
                    displayEmpty
                    value={riskType}
                    onChange={(e) => setRiskType(e.target.value as number)}
                    sx={{
                      ...inputStyle,
                      height: '48px'
                    }}
                  >
                    <MenuItem value="" disabled>
                      Select risk type
                    </MenuItem>
                    <MenuItem value={RISK_TYPE.FIXED_LOT}>Fixed Lot</MenuItem>
                    <MenuItem value={RISK_TYPE.LOT_MULTIPLIER}>Lot Multiplier</MenuItem>
                    <MenuItem value={RISK_TYPE.BALANCE_MULTIPLIER}>Balance Multiplier</MenuItem>
                    <MenuItem value={RISK_TYPE.FIXED_BALANCE_MULTIPLIER}>Fixed Balance Multiplier</MenuItem>
                    <MenuItem value={RISK_TYPE.BALANCE_PERCENTAGE}>Fixed % risk</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box flex={1} width="100%" display="flex" flexDirection="column" gap={2}>
                {riskType === RISK_TYPE.FIXED_BALANCE_MULTIPLIER ? (
                  <>
                    <Box width="100%">
                      <Typography
                        variant="subtitle2"
                        fontWeight={500}
                        mb={1}
                        color="textSecondary"
                        fontSize={{ xs: 13, sm: 14, md: 15, lg: 16 }}
                        lineHeight="100%"
                      >
                        Fixed Master Balance
                      </Typography>
                      <TextField
                        variant="outlined"
                        fullWidth
                        placeholder="0.00"
                        type="number"
                        value={fixedMasterBalance}
                        onChange={(e) => setFixedMasterBalance(e.target.value)}
                        sx={{
                          ...inputStyle,
                          '& .MuiOutlinedInput-root': {
                            ...inputStyle['& .MuiOutlinedInput-root'],
                            height: '48px'
                          }
                        }}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">$</InputAdornment>
                        }}
                      />
                    </Box>
                    <Box width="100%">
                      <Typography
                        variant="subtitle2"
                        fontWeight={500}
                        mb={1}
                        color="textSecondary"
                        fontSize={{ xs: 13, sm: 14, md: 15, lg: 16 }}
                        lineHeight="100%"
                      >
                        Risk Setting - 100% = 1 to 1 risk
                      </Typography>
                      <TextField
                        variant="outlined"
                        fullWidth
                        placeholder="0"
                        type="number"
                        value={riskSettings}
                        onChange={(e) => setRiskSettings(e.target.value)}
                        sx={{
                          ...inputStyle,
                          '& .MuiOutlinedInput-root': {
                            ...inputStyle['& .MuiOutlinedInput-root'],
                            height: '48px'
                          }
                        }}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">%</InputAdornment>
                        }}
                      />
                    </Box>
                  </>
                ) : riskType === RISK_TYPE.FIXED_LOT ? (
                  <Box width="100%">
                    <Typography
                      variant="subtitle2"
                      fontWeight={500}
                      mb={1}
                      color="textSecondary"
                      fontSize={{ xs: 13, sm: 14, md: 15, lg: 16 }}
                      lineHeight="100%"
                    >
                      Lot Size
                    </Typography>
                    <TextField
                      variant="outlined"
                      fullWidth
                      placeholder="0.00"
                      type="number"
                      value={riskSettingsFixedLot}
                      onChange={(e) => setRiskSettingsFixedLot(e.target.value)}
                      sx={{
                        ...inputStyle,
                        '& .MuiOutlinedInput-root': {
                          ...inputStyle['& .MuiOutlinedInput-root'],
                          height: '48px'
                        }
                      }}
                    />
                  </Box>
                ) : riskType === RISK_TYPE.BALANCE_PERCENTAGE ? (
                  <Box width="100%">
                    <Typography
                      variant="subtitle2"
                      fontWeight={500}
                      mb={1}
                      color="textSecondary"
                      fontSize={{ xs: 13, sm: 14, md: 15, lg: 16 }}
                      lineHeight="100%"
                    >
                      Balance percentage
                    </Typography>
                    <TextField
                      variant="outlined"
                      fullWidth
                      placeholder="0"
                      type="number"
                      value={riskSettingsBalancePercentage}
                      onChange={(e) => setRiskSettingsBalancePercentage(e.target.value)}
                      sx={{
                        ...inputStyle,
                        '& .MuiOutlinedInput-root': {
                          ...inputStyle['& .MuiOutlinedInput-root'],
                          height: '48px'
                        }
                      }}
                    />
                  </Box>
                ) : (
                  <Box width="100%">
                    <Typography
                      variant="subtitle2"
                      fontWeight={500}
                      mb={1}
                      color="textSecondary"
                      fontSize={{ xs: 13, sm: 14, md: 15, lg: 16 }}
                      lineHeight="100%"
                    >
                      Risk Setting - 100% = 1 to 1 risk
                    </Typography>
                    <TextField
                      variant="outlined"
                      fullWidth
                      placeholder="0"
                      type="number"
                      value={riskSettings}
                      onChange={(e) => setRiskSettings(e.target.value)}
                      sx={{
                        ...inputStyle,
                        '& .MuiOutlinedInput-root': {
                          ...inputStyle['& .MuiOutlinedInput-root'],
                          height: '48px'
                        }
                      }}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">%</InputAdornment>
                      }}
                    />
                  </Box>
                )}
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>

        <Accordion expanded={expandedPanel === 'panel2'} onChange={() => handleAccordionChange('panel2')} sx={accordionStyle}>
          <AccordionSummary sx={accordionSummaryStyle} expandIcon={<ChevronIcon />} aria-controls="panel2-content" id="panel2-header">
            <Typography component="span" color="textPrimary" fontSize={{ xs: 13, sm: 14, md: 15, lg: 16 }} fontWeight="600">
              SL and TP Settings
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={accordionDetailsStyle}>
            <Box width="100%" display="flex" flexDirection="column" gap={2}>
              <Box display="flex" flexWrap="wrap" gap={2}>
                <FormControlLabel
                  control={<Checkbox checked={copyStopLoss} disabled={strictClose} onChange={(e) => {
                    const checked = e.target.checked;
                    if (checked) {
                      setFixedStopLoss('No');
                      setFixedStopLossValue('');
                    } else {
                      setStopLossRefinement('No');
                      setStopLossRefinementValue('');
                    }
                    setCopyStopLoss(checked);
                  }} />}
                  label={<Typography fontSize={{ xs: 13, sm: 14, md: 15, lg: 16 }}>Copy Stop Loss</Typography>}
                />
                <FormControlLabel
                  control={<Checkbox checked={copyTakeProfit} disabled={strictClose} onChange={(e) => {
                    const checked = e.target.checked;
                    if (checked) {
                      setFixedTakeProfit('No');
                      setFixedTakeProfitValue('');
                    } else {
                      setTakeProfitRefinement('No');
                      setTakeProfitRefinementValue('');
                    }
                    setCopyTakeProfit(checked);
                  }} />}
                  label={<Typography fontSize={{ xs: 13, sm: 14, md: 15, lg: 16 }}>Copy Take Profit</Typography>}
                />
                <FormControlLabel
                  control={<Checkbox checked={copyPendingOrders} onChange={(e) => setCopyPendingOrders(e.target.checked)} />}
                  label={<Typography fontSize={{ xs: 13, sm: 14, md: 15, lg: 16 }}>Copy Pending Orders</Typography>}
                />
              </Box>

              <Box
                width="100%"
                display="flex"
                flexDirection={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: 'start', sm: 'start' }}
                gap={2}
              >
                <Box flex={1} width="100%">
                  <Typography
                    variant="subtitle2"
                    fontWeight={500}
                    mb={1}
                    color="textSecondary"
                    fontSize={{ xs: 13, sm: 14, md: 15, lg: 16 }}
                    lineHeight="100%"
                  >
                    Fixed Stop Loss (pips)
                  </Typography>
                  <Tooltip
                    title={copyStopLoss && !strictClose ? 'Disable Copy Stop Loss first' : ''}
                    placement="top"
                  >
                    <FormControl fullWidth disabled={copyStopLoss && !strictClose}>
                      <Select
                        variant="outlined"
                        fullWidth
                        value={fixedStopLoss}
                        onChange={(e) => {
                          const value = e.target.value;
                          setFixedStopLoss(value);
                          if (strictClose) {
                            if (value === 'Yes') {
                              setCopyStopLoss(false);
                            } else {
                              setCopyStopLoss(true);
                            }
                          }
                        }}
                        disabled={copyStopLoss && !strictClose}
                        sx={{
                          ...inputStyle,
                          height: '48px'
                        }}
                      >
                        <MenuItem value="No">No</MenuItem>
                        <MenuItem value="Yes">Yes</MenuItem>
                      </Select>
                    </FormControl>
                  </Tooltip>
                </Box>
                <Box flex={1} width="100%">
                  <Typography
                    variant="subtitle2"
                    fontWeight={500}
                    mb={1}
                    color="textSecondary"
                    fontSize={{ xs: 13, sm: 14, md: 15, lg: 16 }}
                    lineHeight="100%"
                  >
                    Enter Fixed Stop Loss
                  </Typography>
                  <TextField
                    variant="outlined"
                    fullWidth
                    placeholder="Enter Fixed Stop Loss"
                    type="number"
                    value={fixedStopLossValue}
                    onChange={(e) => {
                      setFixedStopLossValue(e.target.value);
                      if (fixedStopLossError) setFixedStopLossError('');
                    }}
                    disabled={(fixedStopLoss === 'No' || copyStopLoss) && !strictClose}
                    error={!!fixedStopLossError}
                    helperText={fixedStopLossError}
                    sx={{
                      ...inputStyle,
                      '& .MuiOutlinedInput-root': {
                        ...inputStyle['& .MuiOutlinedInput-root'],
                        height: '48px'
                      }
                    }}
                  />
                </Box>
              </Box>

              <Box
                width="100%"
                display="flex"
                flexDirection={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: 'start', sm: 'start' }}
                gap={2}
              >
                <Box flex={1} width="100%">
                  <Typography
                    variant="subtitle2"
                    fontWeight={500}
                    mb={1}
                    color="textSecondary"
                    fontSize={{ xs: 13, sm: 14, md: 15, lg: 16 }}
                    lineHeight="100%"
                  >
                    Fixed Take Profit (pips)
                  </Typography>
                  <Tooltip
                    title={copyTakeProfit && !strictClose ? 'Disable Copy Take Profit first' : ''}
                    placement="top"
                  >
                    <FormControl fullWidth disabled={copyTakeProfit && !strictClose}>
                      <Select
                        variant="outlined"
                        fullWidth
                        value={fixedTakeProfit}
                        onChange={(e) => {
                          const value = e.target.value;
                          setFixedTakeProfit(value);
                          if (strictClose) {
                            if (value === 'Yes') {
                              setCopyTakeProfit(false);
                            } else {
                              setCopyTakeProfit(true);
                            }
                          }
                        }}
                        disabled={copyTakeProfit && !strictClose}
                        sx={{
                          ...inputStyle,
                          height: '48px'
                        }}
                      >
                        <MenuItem value="No">No</MenuItem>
                        <MenuItem value="Yes">Yes</MenuItem>
                      </Select>
                    </FormControl>
                  </Tooltip>
                </Box>
                <Box flex={1} width="100%">
                  <Typography
                    variant="subtitle2"
                    fontWeight={500}
                    mb={1}
                    color="textSecondary"
                    fontSize={{ xs: 13, sm: 14, md: 15, lg: 16 }}
                    lineHeight="100%"
                  >
                    Enter Fixed Take Profit
                  </Typography>
                  <TextField
                    variant="outlined"
                    fullWidth
                    placeholder="Enter Fixed Take Profit"
                    type="number"
                    value={fixedTakeProfitValue}
                    onChange={(e) => {
                      setFixedTakeProfitValue(e.target.value);
                      if (fixedTakeProfitError) setFixedTakeProfitError('');
                    }}
                    disabled={(fixedTakeProfit === 'No' || copyTakeProfit) && !strictClose}
                    error={!!fixedTakeProfitError}
                    helperText={fixedTakeProfitError}
                    sx={{
                      ...inputStyle,
                      '& .MuiOutlinedInput-root': {
                        ...inputStyle['& .MuiOutlinedInput-root'],
                        height: '48px'
                      }
                    }}
                  />
                </Box>
              </Box>

              <Box
                width="100%"
                display="flex"
                flexDirection={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: 'start', sm: 'start' }}
                gap={2}
              >
                <Box flex={1} width="100%">
                  <Typography
                    variant="subtitle2"
                    fontWeight={500}
                    mb={1}
                    color="textSecondary"
                    fontSize={{ xs: 13, sm: 14, md: 15, lg: 16 }}
                    lineHeight="100%"
                  >
                    Stop Loss Refinement (pips)
                  </Typography>
                  <FormControl fullWidth disabled={!copyStopLoss}>
                    <Select
                      variant="outlined"
                      fullWidth
                      value={stopLossRefinement}
                      onChange={(e) => setStopLossRefinement(e.target.value)}
                      disabled={!copyStopLoss}
                      sx={{
                        ...inputStyle,
                        height: '48px'
                      }}
                    >
                      <MenuItem value="No">No</MenuItem>
                      <MenuItem value="Yes">Yes</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Box flex={1} width="100%">
                  <Typography
                    variant="subtitle2"
                    fontWeight={500}
                    mb={1}
                    color="textSecondary"
                    fontSize={{ xs: 13, sm: 14, md: 15, lg: 16 }}
                    lineHeight="100%"
                  >
                    Enter Refinement
                  </Typography>
                  <TextField
                    variant="outlined"
                    fullWidth
                    placeholder="Enter Refinement"
                    type="number"
                    value={stopLossRefinementValue}
                    onChange={(e) => {
                      setStopLossRefinementValue(e.target.value);
                      if (stopLossRefinementError) setStopLossRefinementError('');
                    }}
                    disabled={stopLossRefinement === 'No' || !copyStopLoss}
                    error={!!stopLossRefinementError}
                    helperText={stopLossRefinementError}
                    sx={{
                      ...inputStyle,
                      '& .MuiOutlinedInput-root': {
                        ...inputStyle['& .MuiOutlinedInput-root'],
                        height: '48px'
                      }
                    }}
                  />
                </Box>
              </Box>

              <Box
                width="100%"
                display="flex"
                flexDirection={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: 'start', sm: 'start' }}
                gap={2}
              >
                <Box flex={1} width="100%">
                  <Typography
                    variant="subtitle2"
                    fontWeight={500}
                    mb={1}
                    color="textSecondary"
                    fontSize={{ xs: 13, sm: 14, md: 15, lg: 16 }}
                    lineHeight="100%"
                  >
                    Take Profit Refinement (pips)
                  </Typography>
                  <FormControl fullWidth disabled={!copyTakeProfit}>
                    <Select
                      variant="outlined"
                      fullWidth
                      value={takeProfitRefinement}
                      onChange={(e) => setTakeProfitRefinement(e.target.value)}
                      disabled={!copyTakeProfit}
                      sx={{
                        ...inputStyle,
                        height: '48px'
                      }}
                    >
                      <MenuItem value="No">No</MenuItem>
                      <MenuItem value="Yes">Yes</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Box flex={1} width="100%">
                  <Typography
                    variant="subtitle2"
                    fontWeight={500}
                    mb={1}
                    color="textSecondary"
                    fontSize={{ xs: 13, sm: 14, md: 15, lg: 16 }}
                    lineHeight="100%"
                  >
                    Enter Refinement
                  </Typography>
                  <TextField
                    variant="outlined"
                    fullWidth
                    placeholder="Enter Refinement"
                    type="number"
                    value={takeProfitRefinementValue}
                    onChange={(e) => {
                      setTakeProfitRefinementValue(e.target.value);
                      if (takeProfitRefinementError) setTakeProfitRefinementError('');
                    }}
                    disabled={takeProfitRefinement === 'No' || !copyTakeProfit}
                    error={!!takeProfitRefinementError}
                    helperText={takeProfitRefinementError}
                    sx={{
                      ...inputStyle,
                      '& .MuiOutlinedInput-root': {
                        ...inputStyle['& .MuiOutlinedInput-root'],
                        height: '48px'
                      }
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>
        <Accordion expanded={expandedPanel === 'panel3'} onChange={() => handleAccordionChange('panel3')} sx={accordionStyle}>
          <AccordionSummary sx={accordionSummaryStyle} expandIcon={<ChevronIcon />} aria-controls="panel3-content" id="panel3-header">
            <Typography component="span" color="textPrimary" fontSize={{ xs: 13, sm: 14, md: 15, lg: 16 }} fontWeight="600">
              Advanced Settings
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={accordionDetailsStyle}>
            <Box width="100%" display="flex" flexDirection="column" gap={2}>
              <Box display="flex" flexWrap="wrap" gap={2}>
                <FormControlLabel
                  control={<Checkbox checked={forceMinimumLotSize} onChange={(e) => setForceMinimumLotSize(e.target.checked)} />}
                  label={<Typography fontSize={{ xs: 13, sm: 14, md: 15, lg: 16 }}>Force Minimum Lot Size</Typography>}
                />
                <FormControlLabel
                  control={<Checkbox checked={forceMaximumLotSize} onChange={(e) => setForceMaximumLotSize(e.target.checked)} />}
                  label={<Typography fontSize={{ xs: 13, sm: 14, md: 15, lg: 16 }}>Force Maximum Lot Size</Typography>}
                />
                <FormControlLabel
                  control={<Checkbox checked={reverseTrade} onChange={(e) => setReverseTrade(e.target.checked)} />}
                  label={<Typography fontSize={{ xs: 13, sm: 14, md: 15, lg: 16 }}>Reverse Trade</Typography>}
                />
              </Box>

              <Box display="flex" flexWrap="wrap" gap={2}>
                <FormControlLabel
                  control={<Checkbox checked={contractAlignment} onChange={(e) => setContractAlignment(e.target.checked)} />}
                  label={<Typography fontSize={{ xs: 13, sm: 14, md: 15, lg: 16 }}>Contract Alignment</Typography>}
                />
                <FormControlLabel
                  control={<Checkbox checked={copyExpiryTime} onChange={(e) => setCopyExpiryTime(e.target.checked)} />}
                  label={<Typography fontSize={{ xs: 13, sm: 14, md: 15, lg: 16 }}>Copy Expiry Time</Typography>}
                />
                <FormControlLabel
                  control={<Checkbox checked={strictClose} onChange={(e) => {
                    const checked = e.target.checked;
                    setStrictClose(checked);
                    if (checked) {
                      if (fixedStopLoss === 'No') {
                        setCopyStopLoss(true);
                      }
                      if (fixedTakeProfit === 'No') {
                        setCopyTakeProfit(true);
                      }
                    }
                  }} />}
                  label={
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Typography fontSize={{ xs: 13, sm: 14, md: 15, lg: 16 }}>Strict Close</Typography>
                      <Tooltip
                        title={
                          <Typography
                            color={theme.palette.mode === 'dark' ? theme.palette.background.paper : theme.typography.caption.color}
                            variant={'caption'}
                          >
                            High risk option, click to learn more
                          </Typography>
                        }
                      >
                        <WarningOutlined
                          style={{ color: theme.palette.error.main, fontSize: '14px' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open('https://docs.tradersconnect.com/trade-copier/copier-options/#strict-close', '_blank');
                          }}
                        />
                      </Tooltip>
                    </Box>
                  }
                />
              </Box>

              <Box
                width="100%"
                display="flex"
                flexDirection={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: 'start', sm: 'start' }}
                gap={2}
              >
                <Box flex={1} width="100%">
                  <Typography
                    variant="subtitle2"
                    fontWeight={500}
                    mb={1}
                    color="textSecondary"
                    fontSize={{ xs: 13, sm: 14, md: 15, lg: 16 }}
                    lineHeight="100%"
                  >
                    Copy Trade Comment
                  </Typography>
                  <FormControl fullWidth>
                    <Select
                      variant="outlined"
                      fullWidth
                      value={copyTradeComment}
                      onChange={(e) => {
                        setCopyTradeComment(e.target.value);
                        if (e.target.value !== 'Custom') {
                          setCustomComment('');
                        }
                      }}
                      sx={{
                        ...inputStyle,
                        height: '48px'
                      }}
                    >
                      <MenuItem value="No">No</MenuItem>
                      <MenuItem value="Yes">Yes</MenuItem>
                      <MenuItem value="Custom">Custom</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Box flex={1} width="100%">
                  <Typography
                    variant="subtitle2"
                    fontWeight={500}
                    mb={1}
                    color="textSecondary"
                    fontSize={{ xs: 13, sm: 14, md: 15, lg: 16 }}
                    lineHeight="100%"
                  >
                    Enter Custom Comment
                  </Typography>
                  <TextField
                    variant="outlined"
                    fullWidth
                    placeholder="Enter Custom Comment"
                    value={customComment}
                    onChange={(e) => setCustomComment(e.target.value)}
                    disabled={copyTradeComment !== 'Custom'}
                    sx={{
                      ...inputStyle,
                      '& .MuiOutlinedInput-root': {
                        ...inputStyle['& .MuiOutlinedInput-root'],
                        height: '48px'
                      }
                    }}
                  />
                </Box>
              </Box>

              <Box
                width="100%"
                display="flex"
                flexDirection={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: 'start', sm: 'start' }}
                gap={2}
              >
                <Box flex={1} width="100%">
                  <Typography
                    variant="subtitle2"
                    fontWeight={500}
                    mb={1}
                    color="textSecondary"
                    fontSize={{ xs: 13, sm: 14, md: 15, lg: 16 }}
                    lineHeight="100%"
                  >
                    Copy Magic Number
                  </Typography>
                  <FormControl fullWidth>
                    <Select
                      variant="outlined"
                      fullWidth
                      value={copyMagicNumber}
                      onChange={(e) => setCopyMagicNumber(e.target.value)}
                      sx={{
                        ...inputStyle,
                        height: '48px'
                      }}
                    >
                      <MenuItem value="No">No</MenuItem>
                      <MenuItem value="Yes">Yes</MenuItem>
                      <MenuItem value="Custom">Custom</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Box flex={1} width="100%">
                  <Typography
                    variant="subtitle2"
                    fontWeight={500}
                    mb={1}
                    color="textSecondary"
                    fontSize={{ xs: 13, sm: 14, md: 15, lg: 16 }}
                    lineHeight="100%"
                  >
                    Enter Magic Number
                  </Typography>
                  <TextField
                    variant="outlined"
                    fullWidth
                    placeholder="Enter Magic Number"
                    type="number"
                    value={magicNumber}
                    onChange={(e) => setMagicNumber(e.target.value)}
                    disabled={copyMagicNumber !== 'Custom'}
                    sx={{
                      ...inputStyle,
                      '& .MuiOutlinedInput-root': {
                        ...inputStyle['& .MuiOutlinedInput-root'],
                        height: '48px'
                      }
                    }}
                  />
                </Box>
              </Box>

              <Box
                width="100%"
                display="flex"
                flexDirection={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: 'start', sm: 'start' }}
                gap={2}
              >
                <Box flex={1} width="100%">
                  <Typography
                    variant="subtitle2"
                    fontWeight={500}
                    mb={1}
                    color="textSecondary"
                    fontSize={{ xs: 13, sm: 14, md: 15, lg: 16 }}
                    lineHeight="100%"
                  >
                    Delay type
                  </Typography>
                  <FormControl fullWidth>
                    <Select
                      variant="outlined"
                      fullWidth
                      value={delayType}
                      onChange={(e) => {
                        setDelayType(e.target.value);
                        setTradeDelayMin('');
                        setTradeDelayMax('');
                      }}
                      sx={{
                        ...inputStyle,
                        height: '48px'
                      }}
                    >
                      <MenuItem value="no">No trade delay</MenuItem>
                      <MenuItem value="fixed">Fixed delay</MenuItem>
                      <MenuItem value="random">Random delay</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Box flex={1} width="100%">
                  <Typography
                    variant="subtitle2"
                    fontWeight={500}
                    mb={1}
                    color="textSecondary"
                    fontSize={{ xs: 13, sm: 14, md: 15, lg: 16 }}
                    lineHeight="100%"
                  >
                    Trade Delay (ms){delayType === 'random' ? ' - Minimum' : ''}
                  </Typography>
                  <TextField
                    variant="outlined"
                    fullWidth
                    placeholder={`Trade Delay (ms)${delayType === 'random' ? ' - Minimum' : ''}`}
                    type="number"
                    value={tradeDelayMin}
                    onChange={(e) => {
                      const numericInput = e.target.value.replace(/\D/g, '');
                      setTradeDelayMin(numericInput);
                      if (delayType === 'fixed') {
                        setTradeDelayMax(numericInput);
                      }
                    }}
                    disabled={delayType === 'no'}
                    sx={{
                      ...inputStyle,
                      '& .MuiOutlinedInput-root': {
                        ...inputStyle['& .MuiOutlinedInput-root'],
                        height: '48px'
                      }
                    }}
                  />
                </Box>
              </Box>

              {delayType === 'random' && (
                <Box
                  width="100%"
                  display="flex"
                  flexDirection={{ xs: 'column', sm: 'row' }}
                  alignItems={{ xs: 'start', sm: 'start' }}
                  gap={2}
                >
                  <Box flex={1} width="100%">
                    <Typography
                      variant="subtitle2"
                      fontWeight={500}
                      mb={1}
                      color="textSecondary"
                      fontSize={{ xs: 13, sm: 14, md: 15, lg: 16 }}
                      lineHeight="100%"
                    >
                      Trade Delay (ms) - Maximum
                    </Typography>
                    <TextField
                      variant="outlined"
                      fullWidth
                      placeholder="Trade Delay (ms) - Maximum"
                      type="number"
                      value={tradeDelayMax}
                      onChange={(e) => {
                        const numericInput = e.target.value.replace(/\D/g, '');
                        setTradeDelayMax(numericInput);
                      }}
                      sx={{
                        ...inputStyle,
                        '& .MuiOutlinedInput-root': {
                          ...inputStyle['& .MuiOutlinedInput-root'],
                          height: '48px'
                        }
                      }}
                    />
                  </Box>
                  <Box flex={1} width="100%"></Box>
                </Box>
              )}

              <Box width="100%">
                <Typography
                  variant="subtitle2"
                  fontWeight={500}
                  mb={1}
                  color="textSecondary"
                  fontSize={{ xs: 13, sm: 14, md: 15, lg: 16 }}
                  lineHeight="100%"
                >
                  Lot Refiner
                </Typography>
                <TextField
                  variant="outlined"
                  fullWidth
                  placeholder="Lot Refiner"
                  type="number"
                  value={lotRefiner}
                  onChange={(e) => setLotRefiner(e.target.value)}
                  sx={{
                    ...inputStyle,
                    '& .MuiOutlinedInput-root': {
                      ...inputStyle['& .MuiOutlinedInput-root'],
                      height: '48px'
                    }
                  }}
                />
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>
        <Accordion expanded={expandedPanel === 'panel4'} onChange={() => handleAccordionChange('panel4')} sx={accordionStyle}>
          <AccordionSummary sx={accordionSummaryStyle} expandIcon={<ChevronIcon />} aria-controls="panel4-content" id="panel4-header">
            <Typography component="span" color="textPrimary" fontSize={{ xs: 13, sm: 14, md: 15, lg: 16 }} fontWeight="600">
              Trade Filters
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={accordionDetailsStyle}>
            <Box width="100%" display="flex" flexDirection="column" gap={2}>
              <Box
                width="100%"
                display="flex"
                flexDirection={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: 'start', sm: 'start' }}
                gap={2}
              >
                <Box flex={1} width="100%">
                  <Typography
                    variant="subtitle2"
                    fontWeight={500}
                    mb={1}
                    color="textSecondary"
                    fontSize={{ xs: 13, sm: 14, md: 15, lg: 16 }}
                    lineHeight="100%"
                  >
                    Filter by magic number
                  </Typography>
                  <FormControl fullWidth>
                    <Select
                      variant="outlined"
                      fullWidth
                      displayEmpty
                      value={filterByMagicNumber}
                      onChange={(e) => setFilterByMagicNumber(e.target.value)}
                      sx={{
                        ...inputStyle,
                        height: '48px'
                      }}
                    >
                      <MenuItem value="">Select an option</MenuItem>
                      <MenuItem value="COPY">Copy</MenuItem>
                      <MenuItem value="IGNORE">Ignore</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Box flex={1} width="100%">
                  <Typography
                    variant="subtitle2"
                    fontWeight={500}
                    mb={1}
                    color="textSecondary"
                    fontSize={{ xs: 13, sm: 14, md: 15, lg: 16 }}
                    lineHeight="100%"
                  >
                    Magic Numbers
                  </Typography>
                  <Autocomplete
                    multiple
                    open={false}
                    popupIcon={null}
                    options={selectedMagicNumbers}
                    value={selectedMagicNumbers}
                    getOptionLabel={(option: any) => option}
                    disabled={!filterByMagicNumber}
                    readOnly={!filterByMagicNumber}
                    onChange={(event, value) => {
                      setSelectedMagicNumbers(value);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Press enter to add"
                        value={currentMagicNumber}
                        type="number"
                        sx={{
                          ...inputStyle,
                          '& .MuiOutlinedInput-root': {
                            ...inputStyle['& .MuiOutlinedInput-root']
                          },
                          '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                            WebkitAppearance: 'none',
                            margin: 0
                          },
                          '& input[type=number]': {
                            MozAppearance: 'textfield'
                          }
                        }}
                        onChange={(e) => {
                          setCurrentMagicNumber(e.target.value);
                        }}
                        onBlur={() => {
                          if (currentMagicNumber) {
                            setSelectedMagicNumbers((prev) => [...prev, currentMagicNumber]);
                            setCurrentMagicNumber('');
                          }
                        }}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            if (
                              currentMagicNumber &&
                              !selectedMagicNumbers.includes(currentMagicNumber) &&
                              selectedMagicNumbers?.length < 10
                            ) {
                              setSelectedMagicNumbers((prev) => [...prev, currentMagicNumber]);
                              setCurrentMagicNumber('');
                            }
                          }
                        }}
                      />
                    )}
                    sx={{
                      '& .MuiAutocomplete-inputRoot': {
                        flexWrap: 'wrap',
                        gap: 0.5
                      },
                      '& .MuiAutocomplete-tag': {
                        bgcolor: 'primary.lighter',
                        border: '1px solid',
                        borderRadius: 1,
                        height: 32,
                        pl: 1.5,
                        pr: 1.5,
                        lineHeight: '32px',
                        borderColor: 'primary.light',
                        margin: '2px',
                        '& .MuiChip-label': {
                          paddingLeft: 0,
                          paddingRight: 0
                        },
                        '& .MuiSvgIcon-root': {
                          color: 'primary.main',
                          ml: 1,
                          mr: -0.75,
                          '&:hover': {
                            color: 'primary.dark'
                          }
                        }
                      },
                      '& .MuiAutocomplete-input': {
                        minWidth: '150px !important',
                        flexBasis: '150px'
                      }
                    }}
                  />
                </Box>
              </Box>

              <Box
                width="100%"
                display="flex"
                flexDirection={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: 'start', sm: 'start' }}
                gap={2}
              >
                <Box flex={1} width="100%">
                  <Typography
                    variant="subtitle2"
                    fontWeight={500}
                    mb={1}
                    color="textSecondary"
                    fontSize={{ xs: 13, sm: 14, md: 15, lg: 16 }}
                    lineHeight="100%"
                  >
                    Filter by comment
                  </Typography>
                  <FormControl fullWidth>
                    <Select
                      variant="outlined"
                      fullWidth
                      displayEmpty
                      value={filterByComment}
                      onChange={(e) => setFilterByComment(e.target.value)}
                      sx={{
                        ...inputStyle,
                        height: '48px'
                      }}
                    >
                      <MenuItem value="">Select an option</MenuItem>
                      <MenuItem value="COPY">Copy</MenuItem>
                      <MenuItem value="IGNORE">Ignore</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Box flex={1} width="100%">
                  <Typography
                    variant="subtitle2"
                    fontWeight={500}
                    mb={1}
                    color="textSecondary"
                    fontSize={{ xs: 13, sm: 14, md: 15, lg: 16 }}
                    lineHeight="100%"
                  >
                    Comments
                  </Typography>
                  <Autocomplete
                    multiple
                    open={false}
                    popupIcon={null}
                    options={selectedComments}
                    value={selectedComments}
                    getOptionLabel={(option: any) => option}
                    disabled={!filterByComment}
                    readOnly={!filterByComment}
                    onChange={(event, value) => {
                      setSelectedComments(value);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Press enter to add"
                        value={currentComment}
                        sx={{
                          ...inputStyle,
                          '& .MuiOutlinedInput-root': {
                            ...inputStyle['& .MuiOutlinedInput-root']
                          },
                          '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                            WebkitAppearance: 'none',
                            margin: 0
                          },
                          '& input[type=number]': {
                            MozAppearance: 'textfield'
                          }
                        }}
                        onChange={(e) => {
                          setCurrentComment(e.target.value);
                        }}
                        onBlur={() => {
                          if (currentComment) {
                            setSelectedComments((prev) => [...prev, currentComment]);
                            setCurrentComment('');
                          }
                        }}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            if (currentComment && !selectedComments.includes(currentComment) && selectedComments.length < 10) {
                              setSelectedComments((prev) => [...prev, currentComment]);
                              setCurrentComment('');
                            }
                          }
                        }}
                      />
                    )}
                    sx={{
                      '& .MuiAutocomplete-inputRoot': {
                        flexWrap: 'wrap',
                        gap: 0.5
                      },
                      '& .MuiAutocomplete-tag': {
                        bgcolor: 'primary.lighter',
                        border: '1px solid',
                        borderRadius: 1,
                        height: 32,
                        pl: 1.5,
                        pr: 1.5,
                        lineHeight: '32px',
                        borderColor: 'primary.light',
                        margin: '2px',
                        '& .MuiChip-label': {
                          paddingLeft: 0,
                          paddingRight: 0
                        },
                        '& .MuiSvgIcon-root': {
                          color: 'primary.main',
                          ml: 1,
                          mr: -0.75,
                          '&:hover': {
                            color: 'primary.dark'
                          }
                        }
                      },
                      '& .MuiAutocomplete-input': {
                        minWidth: '150px !important',
                        flexBasis: '150px'
                      }
                    }}
                  />
                </Box>
              </Box>

              <Box
                width="100%"
                display="flex"
                flexDirection={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: 'start', sm: 'start' }}
                gap={2}
              >
                <Box flex={1} width="100%">
                  <Typography
                    variant="subtitle2"
                    fontWeight={500}
                    mb={1}
                    color="textSecondary"
                    fontSize={{ xs: 13, sm: 14, md: 15, lg: 16 }}
                    lineHeight="100%"
                  >
                    Trade Side
                  </Typography>
                  <FormControl fullWidth>
                    <Select
                      variant="outlined"
                      fullWidth
                      value={tradeSide}
                      onChange={(e) => setTradeSide(e.target.value)}
                      sx={{
                        ...inputStyle,
                        height: '48px'
                      }}
                    >
                      <MenuItem value="ALL">Copy All trades</MenuItem>
                      <MenuItem value="BUY">Buy Only</MenuItem>
                      <MenuItem value="SELL">Sell Only</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Box flex={1} width="100%">
                  <Typography
                    variant="subtitle2"
                    fontWeight={500}
                    mb={1}
                    color="textSecondary"
                    fontSize={{ xs: 13, sm: 14, md: 15, lg: 16 }}
                    lineHeight="100%"
                  >
                    Filter by lot size
                  </Typography>
                  <FormControl fullWidth>
                    <Select
                      variant="outlined"
                      fullWidth
                      displayEmpty
                      value={filterByLotSize}
                      onChange={(e) => setFilterByLotSize(e.target.value)}
                      sx={{
                        ...inputStyle,
                        height: '48px'
                      }}
                    >
                      <MenuItem value="No">No</MenuItem>
                      <MenuItem value="Yes">Yes</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Box>

              {filterByLotSize === 'Yes' && (
                <Box
                  width="100%"
                  display="flex"
                  flexDirection={{ xs: 'column', sm: 'row' }}
                  alignItems={{ xs: 'start', sm: 'start' }}
                  gap={2}
                >
                  <Box flex={1} width="100%">
                    <Typography
                      variant="subtitle2"
                      fontWeight={500}
                      mb={1}
                      color="textSecondary"
                      fontSize={{ xs: 13, sm: 14, md: 15, lg: 16 }}
                      lineHeight="100%"
                    >
                      Enter minimum lot size
                    </Typography>
                    <TextField
                      variant="outlined"
                      fullWidth
                      placeholder="Enter minimum lot size"
                      type="number"
                      value={minLotFilter}
                      onChange={(e) => setMinLotFilter(e.target.value)}
                      sx={{
                        ...inputStyle,
                        '& .MuiOutlinedInput-root': {
                          ...inputStyle['& .MuiOutlinedInput-root'],
                          height: '48px'
                        }
                      }}
                    />
                  </Box>
                  <Box flex={1} width="100%">
                    <Typography
                      variant="subtitle2"
                      fontWeight={500}
                      mb={1}
                      color="textSecondary"
                      fontSize={{ xs: 13, sm: 14, md: 15, lg: 16 }}
                      lineHeight="100%"
                    >
                      Enter maximum lot size
                    </Typography>
                    <TextField
                      variant="outlined"
                      fullWidth
                      placeholder="Enter maximum lot size"
                      type="number"
                      value={maxLotFilter}
                      onChange={(e) => setMaxLotFilter(e.target.value)}
                      sx={{
                        ...inputStyle,
                        '& .MuiOutlinedInput-root': {
                          ...inputStyle['& .MuiOutlinedInput-root'],
                          height: '48px'
                        }
                      }}
                    />
                  </Box>
                </Box>
              )}
            </Box>
          </AccordionDetails>
        </Accordion>
      </Box>
      <Modal
        open={openRiskCalculator}
        onClose={() => setOpenRiskCalculator(false)}
        aria-labelledby="risk-calculator-modal"
        aria-describedby="risk-calculator-modal-description"
        sx={{ zIndex: (theme) => theme.zIndex.modal + 1000 }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            minWidth: 540,
            maxWidth: 620
          }}
        >
          <RiskCalculatorModal
            handleClose={() => setOpenRiskCalculator(false)}
            handleSuccess={(riskTypeResult: 'percentage' | 'lot' | 'fixedLot' | '' | null, value: string) => {
              if (riskTypeResult) {
                setRiskType(
                  riskTypeResult === 'percentage'
                    ? RISK_TYPE.BALANCE_MULTIPLIER
                    : riskTypeResult === 'lot'
                    ? RISK_TYPE.LOT_MULTIPLIER
                    : RISK_TYPE.FIXED_LOT
                );
                if (riskTypeResult !== 'fixedLot') {
                  setRiskSettings(value);
                } else {
                  setRiskSettingsFixedLot(value);
                }
                if (riskTypeResult === 'percentage') {
                  setContractAlignment(true);
                }
              }
              setOpenRiskCalculator(false);
            }}
          />
        </Box>
      </Modal>
    </GlobalDialog>
  );
};

export default CopierEditDialog;
